/**
 * Phase 21.4 Batch 3E — Admin master ON/OFF toggle for the Contest
 * Center feature flag.
 *
 * Mounts on the Admin Contests page (`/admin/contests`). Uses
 * `PUT /api/admin/homepage/contests-toggle`. Confirmation before every
 * state change.
 */
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

export const ContestFlagToggle = () => {
  const [enabled, setEnabled] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/homepage/config").then((r) => {
      setEnabled(!!r.data?.contests?.enabled);
    }).catch(() => setEnabled(false));
  }, []);

  const toggle = async () => {
    if (enabled == null) return;
    const next = !enabled;
    const msg = next
      ? "Turn Contest Center ON? The public /contests page + entry flow become available immediately."
      : "Turn Contest Center OFF? The public site drops back to the 'Coming Soon' placeholder. Admin management stays available.";
    if (!window.confirm(msg)) return;
    setSaving(true);
    try {
      const r = await api.put("/admin/homepage/contests-toggle", { enabled: next });
      const val = !!r.data?.contests?.enabled;
      setEnabled(val);
      toast.success(val ? "Contest Center is LIVE 🏆" : "Contest Center is HIDDEN");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally {
      setSaving(false);
    }
  };

  if (enabled == null) {
    return (
      <div className="text-xs text-gray-500" data-testid="admin-contest-flag-loading">Loading flag…</div>
    );
  }

  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2" data-testid="admin-contest-flag-panel">
      <span
        className={`text-[10px] px-2 py-1 rounded-full uppercase tracking-wider font-black ${enabled ? "bg-emerald-500/20 text-emerald-200 border border-emerald-500/40" : "bg-white/10 text-gray-300 border border-white/15"}`}
        data-testid="admin-contest-flag-status"
      >
        Contest Center · {enabled ? "LIVE" : "HIDDEN"}
      </span>
      <Button
        onClick={toggle}
        disabled={saving}
        className={enabled
          ? "bg-rose-600 hover:bg-rose-500 text-white font-black text-xs px-3 py-1 h-auto"
          : "bg-white text-black hover:bg-amber-200 font-black text-xs px-3 py-1 h-auto"}
        data-testid="admin-contest-flag-toggle-btn"
      >
        {saving ? "…" : enabled ? "Turn OFF" : "Turn ON"}
      </Button>
    </div>
  );
};

export default ContestFlagToggle;
